/**
 * Created by Viacheslav Romanov on 26.10.2026
 *
 * Who is using the admin: somebody from teams.json, signed in through the sso
 * (cleverapps/microservices/sso). Signing in leaves the game for the sso and comes back with
 * `?sso=<ticket>`; the ticket is checked here against the sso's public key - signed by it, made out to
 * this origin, not run out - and the person is kept in a cookie on the game's domain, so every game on
 * it knows them.
 *
 * The check is in the browser only: it keeps strangers out of the admin's screens, while the REST
 * calls the admin makes answer anybody, as they always did.
 */

const AdminAccess = {
    SSO_URL: "https://sso.cleverappssg.com",

    COOKIE_KEY: "admin_sso",
    COOKIE_MAX_AGE: 30 * 24 * 60 * 60,

    userName: undefined,

    init: function () {
        const ticket = this.takeTicket();

        if (ticket) {
            this.check(ticket).then((payload) => {
                if (payload) {
                    this.writeData({ user: payload.sub, name: payload.name });
                    this.setUser(payload.sub);
                } else {
                    cleverapps.notification.create("Admin sign-in failed, try again");
                }
            }).catch((e) => {
                console.error("admin sign-in", e);
                cleverapps.notification.create("Admin sign-in failed, try again");
            });
            return;
        }

        const data = this.readData();
        if (data && data.user) {
            this.setUser(data.user);
        }
    },

    setUser: function (user) {
        this.userName = user;
        if (cleverapps.snapshotBar) {
            cleverapps.snapshotBar.updateAdminButton();
        }
    },

    // off to the sso, which comes back to this very page
    login: function () {
        window.location.assign(`${this.SSO_URL}/login?return=${encodeURIComponent(window.location.href)}`);
    },

    // the ticket the sso came back with, taken out of the address so a reload or a shared link does not carry it
    takeTicket: function () {
        const url = new URL(window.location.href);
        const ticket = url.searchParams.get("sso");

        if (ticket) {
            url.searchParams.delete("sso");
            window.history.replaceState(null, "", url.toString());
        }

        return ticket;
    },

    check: function (ticket) {
        const parts = ticket.split(".");
        if (parts.length !== 2 || !window.crypto || !window.crypto.subtle) {
            return Promise.resolve(undefined);
        }

        return fetch(`${this.SSO_URL}/key`).then((response) => response.text()).then((pem) => {
            const der = this.decode(pem.replace(/-----[^-]+-----|\s/g, ""));
            return window.crypto.subtle.importKey("spki", der, { name: "Ed25519" }, false, ["verify"]);
        }).then((key) => window.crypto.subtle.verify({ name: "Ed25519" }, key, this.decode(parts[1]), new TextEncoder().encode(parts[0])))
            .then((valid) => {
                if (!valid) {
                    return undefined;
                }

                const payload = JSON.parse(new TextDecoder().decode(this.decode(parts[0])));
                return payload.aud === window.location.origin && payload.exp * 1000 > Date.now() ? payload : undefined;
            });
    },

    // base64 or base64url to bytes
    decode: function (text) {
        const base64 = text.replace(/-/g, "+").replace(/_/g, "/");
        const binary = window.atob(base64 + "===".substring(0, (4 - base64.length % 4) % 4));
        return Uint8Array.from(binary, (char) => char.charCodeAt(0));
    },

    getCookieDomain: function () {
        const host = window.location.hostname;
        if (!host || host.indexOf(".") === -1 || /^[0-9.]+$/.test(host)) {
            return undefined;
        }
        return host.split(".").slice(-2).join(".");
    },

    readData: function () {
        const prefix = `${this.COOKIE_KEY}=`;
        const cookies = document.cookie ? document.cookie.split("; ") : [];
        const found = cookies.find((cookie) => cookie.indexOf(prefix) === 0);
        if (!found) {
            return undefined;
        }

        try {
            return JSON.parse(decodeURIComponent(found.substring(prefix.length)));
        } catch (e) {
            return undefined;
        }
    },

    writeData: function (data) {
        const domain = this.getCookieDomain();
        const domainAttr = domain ? `; Domain=${domain}` : "";
        const secureAttr = window.location.protocol === "https:" ? "; Secure" : "";

        document.cookie = `${this.COOKIE_KEY}=${encodeURIComponent(JSON.stringify(data))}; Path=/; Max-Age=${this.COOKIE_MAX_AGE}; SameSite=Lax${domainAttr}${secureAttr}`;
    }
};

cleverapps.adminAccess = function () {
    AdminAccess.init();
};
